import { Add, ChevronDown, ChevronUp, Connect } from '@carbon/react/icons';
import {
  Button,
  Checkbox,
  InlineNotification,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@carbon/react';
import { CreateCrmAccount } from 'components/create-crm-account/create-crm-account';
import { EntitySearchTable } from 'components/entity-search-table';
import ErrorToast from 'components/error-toast';
import { PlainLink } from 'components/link';
import { WorkflowStatusPopover } from 'components/workflow-status-popover/workflow-status-popover';
import { useAtom, useSetAtom } from 'jotai';
import { subscribe } from 'lib/pusher';
import { LinkCrmAccountPanel } from 'pages/crm-tracts-subtracts/link-crm-account-panel';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  EntityLinkResponse,
  EntityWithAccountsResponse,
} from 'types/api-responses';
import {
  createCrmAccountMutationAtom,
  createEntitySearchAtom,
  entityLinkingMutationAtom,
  type selectedEntityAtom,
} from './atoms';
import style from './entity-search-with-accounts.module.scss';
import {
  CrmWorkflowProcessStatusChangeData,
  CrmWorkflowStepStatusChangeData,
  EntitySearchWithAccountsParams,
} from './types';
import { STATUS_DISPLAY_MAP } from './utils';

const DEFAULT_PAGE_SIZE = 16;

/**
 * Process EntityLinkResponse and extract step events and link details.
 * @param {EntityLinkResponse} data - The entity link response
 * @return {{ stepEvents, linkDetails, channel }} Processed link data
 */
function processEntityLinkResponse(data: EntityLinkResponse): {
  stepEvents: Array<{
    name: string;
    label: string;
    stepType: string;
    order: number;
  }>;
  linkDetails: {
    status: string;
    stepEvents: Array<{
      name: string;
      label: string;
      stepType: string;
      order: number;
    }>;
    statusItems: Array<{
      label: string;
      value: string;
      details?: string;
      name: string;
    }>;
  };
  channel: string;
} {
  const stepEvents = data.resources.notifications.events.filter(
    (event) => event.step_type
  );
  const initialStatusItems = stepEvents.map((event) => ({
    label: `${event.label}:`,
    value: 'Pending',
    name: event.name,
  }));

  const linkDetails = {
    status: 'pending',
    stepEvents: stepEvents.map((event) => ({
      name: event.name,
      label: event.label,
      stepType: event.step_type || '',
      order: event.order || 0,
    })),
    statusItems: initialStatusItems,
  };

  return {
    stepEvents: linkDetails.stepEvents,
    linkDetails,
    channel: data.resources.notifications.channel,
  };
}

/**
 * Get error message from linking error.
 * @param {unknown} error - The error object
 * @return {string} Error message
 */
function getLinkingErrorMessage(error: unknown): string {
  return error instanceof Error
    ? error.message
    : 'Failed to link entity to CRM account. Please try again.';
}

type SelectedEntityData = Awaited<ReturnType<typeof selectedEntityAtom.read>>;

/** The entity and CRM account the picker currently points at. */
export interface EntityAccountSelection {
  entityId: number | null;
  entityName: string | null;
  /** Salesforce Account Id; empty until the entity has a linked account. */
  accountId: string;
  accountName: string | null;
}

export interface EntityAccountPickerProps {
  accountType: Array<{ value: string; label: string }>;
  accountSource: Array<{ value: string; label: string }>;
  /** A searchAtomPair('entities') the caller owns, one per mounted picker. */
  searchAtom: ReturnType<typeof createEntitySearchAtom>;
  onChange: (selection: EntityAccountSelection) => void;
  /** The account already assigned to the record being edited, if any. */
  assignedAccount?: SelectedEntityData['account'];
  assignedEntities?: SelectedEntityData['entities'];
  /** Keep the search page in the URL (default); see EntitySearchTable. */
  persistentPagination?: boolean;
}

/**
 * Entity search and CRM account management: search Insights entities, pick one
 * (or one of its linked accounts), and link or create a CRM account for an
 * entity that has none. Uses the shared EntitySearchTable for search/table
 * rendering and tracks the link and create workflows over Pusher. Reports the
 * current entity and account through `onChange`; it holds no form state, so the
 * tract wizard and the bulk upload page both drive it.
 * @param {EntityAccountPickerProps} props - Search atom, options, onChange
 * @return {JSX.Element} Entity search with CRM account management
 */
const EntityAccountPicker = ({
  accountType,
  accountSource,
  searchAtom,
  onChange,
  assignedAccount,
  assignedEntities = [],
  persistentPagination,
}: EntityAccountPickerProps) => {
  const [searchResults, setSearchPayload] = useAtom(searchAtom.searchAtom);
  const createCrmAccountMutationState = useSetAtom(
    createCrmAccountMutationAtom
  );
  const entityLinkingMutation = useSetAtom(entityLinkingMutationAtom);

  // CRM-specific local state
  const [selectedEntity, setSelectedEntity] = useState<number | null>(null);
  const [linkedOnly, setLinkedOnly] = useState<boolean>(true);
  const [showLinkPanel, setShowLinkPanel] = useState(false);
  const [showCreatePanel, setShowCreatePanel] = useState(false);
  const [entityLinkStatus, setEntityLinkStatus] = useState<{
    entityId: number | null;
    channel: string | null;
    linkDetails: {
      status: string;
      stepEvents: Array<{
        name: string;
        label: string;
        stepType: string;
        order: number;
      }>;
      statusItems: Array<{
        label: string;
        value: string;
        details?: string;
        name: string;
      }>;
    } | null;
    linkedAccountId: string | null;
  }>({
    entityId: null,
    channel: null,
    linkDetails: null,
    linkedAccountId: null,
  });
  // The latest link status for the Pusher callbacks, whose closures may be older than the render.
  const entityLinkStatusRef = useRef(entityLinkStatus);
  entityLinkStatusRef.current = entityLinkStatus;

  const [isLinkWorkflowComplete, setIsLinkWorkflowComplete] = useState(false);
  const [overallStatus, setOverallStatus] = useState<string>('pending');
  const [linkingError, setLinkingError] = useState<string | null>(null);
  const [linkingSuccess, setLinkingSuccess] = useState(false);
  const [isLinkingAfterCreate, setIsLinkingAfterCreate] = useState(false);
  const [createAccountError, setCreateAccountError] = useState<string | null>(
    null
  );
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [createAccountChannel, setCreateAccountChannel] = useState<
    string | null
  >(null);
  const [createAccountEvents, setCreateAccountEvents] = useState<
    Array<{ name: string; label: string }>
  >([]);
  const [showAccountMismatchWarning, setShowAccountMismatchWarning] =
    useState(false);

  const hasMultipleEntities = assignedEntities.length > 1;
  const [isAssignedEntitiesExpanded, setIsAssignedEntitiesExpanded] =
    useState(false);

  // Helper functions
  const resetCreateAccountState = useCallback(() => {
    setCreateAccountChannel(null);
    setCreateAccountEvents([]);
    setCreateAccountError(null);
    setIsCreatingAccount(false);
  }, []);

  const resetPanelStates = useCallback(() => {
    setShowLinkPanel(false);
    setShowCreatePanel(false);
  }, []);

  const resetLinkingNotifications = useCallback(() => {
    setLinkingError(null);
    setLinkingSuccess(false);
    setIsLinkingAfterCreate(false);
  }, []);

  const resetCreateAccountAndPanels = useCallback(
    () => {
      resetPanelStates();
      resetCreateAccountState();
      resetLinkingNotifications();
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const isLinking = !!(
    entityLinkStatus.channel &&
    entityLinkStatus.linkDetails &&
    !isLinkWorkflowComplete
  );

  const formatStepStatus = (status: string) => {
    return STATUS_DISPLAY_MAP[status] || status;
  };

  // Memoize stepEvents for stable reference
  const stepEvents = useMemo(() => {
    return entityLinkStatus.linkDetails?.stepEvents || [];
  }, [entityLinkStatus.linkDetails?.stepEvents]);

  const stepTypeToIndex = useMemo(() => {
    if (!stepEvents.length) return {};
    const mapping: Record<string, number> = {};
    stepEvents.forEach((event, index) => {
      if (event.stepType) {
        mapping[event.stepType] = index;
      }
    });
    return mapping;
  }, [stepEvents]);

  // Selected entity data for action button logic
  const selectedEntityData = useMemo(() => {
    if (!selectedEntity) return null;
    return (
      searchResults.data?.results.find(
        (e: EntityWithAccountsResponse) => e.id === selectedEntity
      ) || null
    );
  }, [selectedEntity, searchResults.data?.results]);

  // A completed link reports its account by Id; the reloaded results carry its name.
  const linkedAccountAwaitingNameRef = useRef<{
    entityId: number;
    accountId: string;
  } | null>(null);
  useEffect(() => {
    const linked = linkedAccountAwaitingNameRef.current;
    if (!linked || selectedEntityData?.id !== linked.entityId) return;
    const account = selectedEntityData.accounts?.find(
      ({ salesforceId }) => salesforceId === linked.accountId
    );
    if (!account) return;
    linkedAccountAwaitingNameRef.current = null;
    onChange({
      entityId: linked.entityId,
      entityName: selectedEntityData.name,
      accountId: account.salesforceId,
      accountName: account.name,
    });
  }, [selectedEntityData, onChange]);

  const canOpenLinkPanel = useMemo(() => {
    if (!selectedEntityData) return false;
    const hasAccounts =
      selectedEntityData.accounts && selectedEntityData.accounts.length > 0;
    return !hasAccounts;
  }, [selectedEntityData]);

  // Handle workflow completion
  const handleLinkCompletion = useCallback(() => {
    setIsLinkWorkflowComplete(true);
    setLinkingSuccess(true);

    const { linkedAccountId, entityId } = entityLinkStatusRef.current;
    if (linkedAccountId && entityId) {
      onChange({
        entityId,
        entityName: selectedEntityData?.name ?? null,
        accountId: linkedAccountId,
        accountName: null,
      });
      linkedAccountAwaitingNameRef.current = {
        entityId,
        accountId: linkedAccountId,
      };
      setSelectedEntity(entityId);
    }

    const searchParams = searchResults.currentPayload;
    if (searchParams) {
      setSearchPayload(
        (current: EntitySearchWithAccountsParams | undefined) =>
          current && {
            ...current,
            pageSize: searchParams.pageSize || DEFAULT_PAGE_SIZE,
          }
      );
    }
  }, [
    searchResults.currentPayload,
    setSearchPayload,
    onChange,
    selectedEntityData?.name,
  ]);

  // Update step status from Pusher events
  const updateStepStatus = useCallback(
    (stepType: string, status: string, details?: string) => {
      const index = stepTypeToIndex[stepType];
      if (index !== undefined) {
        setEntityLinkStatus((prev) => {
          if (!prev.linkDetails) return prev;

          const updated = { ...prev.linkDetails };
          const currentStatus = updated.statusItems[index]?.value;

          if (currentStatus === 'Completed' && status !== 'processed') {
            return prev;
          }

          updated.statusItems = [...updated.statusItems];
          updated.statusItems[index] = {
            ...updated.statusItems[index],
            value: formatStepStatus(status),
            details: details || undefined,
          };

          return {
            ...prev,
            linkDetails: updated,
          };
        });
      }
    },
    [stepTypeToIndex]
  );

  // Subscribe to Pusher channel for entity linking
  useEffect(() => {
    if (
      !entityLinkStatus.channel ||
      !stepEvents.length ||
      isLinkWorkflowComplete
    ) {
      return;
    }

    const channel = entityLinkStatus.channel;
    const subscription = subscribe(channel);

    const handleProcessStatusChange = (
      data: CrmWorkflowProcessStatusChangeData
    ) => {
      if (data.overall_status) {
        setOverallStatus(data.overall_status);
      }

      if (data.overall_status === 'processed') {
        handleLinkCompletion();
      }
    };

    const handleStepStatusChange = (data: CrmWorkflowStepStatusChangeData) => {
      if (data.step_type && data.status) {
        updateStepStatus(
          data.step_type,
          data.status,
          data.details || undefined
        );
      }
    };

    subscription.bind(
      'crm-workflow-process-status-changed',
      handleProcessStatusChange
    );

    stepEvents.forEach((event) => {
      subscription.bind(event.name, handleStepStatusChange);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [
    entityLinkStatus.channel,
    stepEvents,
    isLinkWorkflowComplete,
    updateStepStatus,
    handleLinkCompletion,
  ]);

  // Subscribe to Pusher channel for account creation workflow
  useEffect(
    () => {
      if (!createAccountChannel || !createAccountEvents.length) {
        return;
      }

      const channel = createAccountChannel;
      const subscription = subscribe(channel);

      const handleProcessStatusChange = (
        data: CrmWorkflowProcessStatusChangeData
      ) => {
        if (data.overall_status === 'processed') {
          setShowCreatePanel(false);
          resetCreateAccountState();
        } else if (data.overall_status === 'failed') {
          setIsCreatingAccount(false);
          const errorMessage =
            data.details || 'Failed to create CRM account. Please try again.';
          setCreateAccountError(errorMessage);
        }
      };

      const handleAccountSalesforceCreateStatusChange = (data: {
        status?: string;
        output?: {
          account?: {
            Id?: string;
            id?: string;
          };
        };
      }) => {
        if (data.status === 'processed') {
          const accountId =
            data.output?.account?.Id || data.output?.account?.id;
          if (accountId && selectedEntity) {
            setIsLinkWorkflowComplete(false);
            setOverallStatus('pending');
            setLinkingError(null);
            setLinkingSuccess(false);
            setIsLinkingAfterCreate(true);
            setEntityLinkStatus({
              entityId: selectedEntity,
              channel: null,
              linkDetails: null,
              linkedAccountId: accountId,
            });

            entityLinkingMutation({
              url: `/entities/${selectedEntity}/entity_links`,
              type: 'POST',
              data: {
                accountId: accountId,
              },
              onSuccess: (data: EntityLinkResponse) => {
                const { linkDetails, channel } =
                  processEntityLinkResponse(data);

                setEntityLinkStatus({
                  entityId: selectedEntity,
                  channel: channel,
                  linkDetails: linkDetails,
                  linkedAccountId: accountId,
                });
                setOverallStatus('pending');
              },
              onError: (error: unknown) => {
                setEntityLinkStatus({
                  entityId: null,
                  channel: null,
                  linkDetails: null,
                  linkedAccountId: null,
                });
                setLinkingError(getLinkingErrorMessage(error));
              },
            });
          }
        }
      };

      subscription.bind(
        'crm-workflow-process-status-changed',
        handleProcessStatusChange
      );

      const eventName = 'crm-account-salesforce-create-status-changed';
      subscription.bind(eventName, handleAccountSalesforceCreateStatusChange);

      return () => {
        subscription.unbind(
          'crm-workflow-process-status-changed',
          handleProcessStatusChange
        );
        subscription.unbind(
          eventName,
          handleAccountSalesforceCreateStatusChange
        );
        subscription.unsubscribe();
      };
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [createAccountChannel, createAccountEvents]
  );

  // Handle entity selection from shared table
  const handleEntitySelect = useCallback(
    (entity: EntityWithAccountsResponse | null) => {
      linkedAccountAwaitingNameRef.current = null;
      setSelectedEntity(entity?.id ?? null);

      const account = entity?.accounts?.[0];
      onChange({
        entityId: entity?.id ?? null,
        entityName: entity?.name ?? null,
        accountId: account?.salesforceId ?? '',
        accountName: account?.name ?? null,
      });

      if (entity?.accounts && entity.accounts.length > 0) {
        // Check account mismatch
        if (
          assignedAccount?.salesforceId &&
          entity.accounts[0].salesforceId &&
          assignedAccount.salesforceId !== entity.accounts[0].salesforceId
        ) {
          setShowAccountMismatchWarning(true);
        } else {
          setShowAccountMismatchWarning(false);
        }
      } else {
        setShowAccountMismatchWarning(false);
      }

      resetCreateAccountAndPanels();

      // Reset entity linking status
      setEntityLinkStatus({
        entityId: null,
        channel: null,
        linkDetails: null,
        linkedAccountId: null,
      });
      setIsLinkWorkflowComplete(false);
      setOverallStatus('pending');
    },
    [onChange, assignedAccount, resetCreateAccountAndPanels]
  );

  // Handle account sub-row selection from shared table
  const handleAccountSelect = useCallback(
    (entity: EntityWithAccountsResponse, accountIndex: number) => {
      linkedAccountAwaitingNameRef.current = null;
      setSelectedEntity(entity.id);
      const account = entity.accounts[accountIndex];
      onChange({
        entityId: entity.id,
        entityName: entity.name,
        accountId: account.salesforceId,
        accountName: account.name,
      });

      if (
        assignedAccount?.salesforceId &&
        account.salesforceId &&
        assignedAccount.salesforceId !== account.salesforceId
      ) {
        setShowAccountMismatchWarning(true);
      } else {
        setShowAccountMismatchWarning(false);
      }
    },
    [onChange, assignedAccount]
  );

  // A toggle of the checkbox re-runs the current search with it; a new search
  // already starts with it (baseParams).
  const payloadLinkedOnly = searchResults.currentPayload?.linkedOnly;
  const hasPayload = !!searchResults.currentPayload;
  useEffect(() => {
    if (!hasPayload || payloadLinkedOnly === linkedOnly) return;
    setSearchPayload(
      (current: EntitySearchWithAccountsParams | undefined) =>
        current && { ...current, linkedOnly }
    );
  }, [hasPayload, payloadLinkedOnly, linkedOnly, setSearchPayload]);

  // Entity link status indicator (shown inline in the UI)
  const showLinkStatusIndicator =
    entityLinkStatus.entityId && entityLinkStatus.linkDetails;

  return (
    <div className={style.entitySearchContainer}>
      <div
        className={
          showLinkPanel || showCreatePanel
            ? style.splitContainer
            : style.entitySearchContent
        }
      >
        <div className={style.leftPanel} data-floating-menu-container>
          {searchResults?.error ? (
            <ErrorToast message={searchResults?.error} />
          ) : null}
          {linkingError ? (
            <div className={style.linkingNotificationContainer}>
              <InlineNotification kind="error" title={linkingError} />
            </div>
          ) : null}
          {linkingSuccess ? (
            <div className={style.linkingNotificationContainer}>
              <InlineNotification
                kind="success"
                lowContrast
                title={
                  isLinkingAfterCreate
                    ? 'Successfully created CRM Account and linked to Insights Entity'
                    : 'Successfully linked Insights Entity to CRM Account'
                }
              />
            </div>
          ) : null}

          {showAccountMismatchWarning && assignedAccount ? (
            <div className={style.linkingNotificationContainer}>
              <InlineNotification
                kind="warning"
                title="Warning"
                subtitle="The entity you've selected is linked to a different CRM account. If you continue, the CRM account assigned to this tract will be overwritten."
                lowContrast={false}
              />
            </div>
          ) : null}

          {/* Entity Link Status Indicator */}
          {showLinkStatusIndicator && entityLinkStatus.linkDetails && (
            <div className={style.linkingNotificationContainer}>
              <WorkflowStatusPopover
                dataTestId="entity-link-status-overflow-menu"
                key={`entity-link-status-${entityLinkStatus.entityId}`}
                statusItems={entityLinkStatus.linkDetails.statusItems}
                overallStatus={overallStatus}
                iconDescription="Entity link status"
                ariaLabel="Show entity link status"
                data-testid="entity-link-status-overflow-menu"
              />
            </div>
          )}

          {/* Assigned CRM Account Table */}
          {assignedAccount && (
            <div className={style.assignedAccountSection}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader>Insights Entity</TableHeader>
                    <TableHeader>Assigned CRM Account</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      {hasMultipleEntities ? (
                        <div className={style.entityInfo}>
                          <Button
                            kind="ghost"
                            size="sm"
                            className={style.expander}
                            aria-label={
                              isAssignedEntitiesExpanded
                                ? 'Collapse entities'
                                : 'Expand entities'
                            }
                            aria-expanded={isAssignedEntitiesExpanded}
                            onClick={() =>
                              setIsAssignedEntitiesExpanded(
                                !isAssignedEntitiesExpanded
                              )
                            }
                            renderIcon={
                              isAssignedEntitiesExpanded
                                ? ChevronUp
                                : ChevronDown
                            }
                          />
                          <span className={style.value}>
                            {assignedEntities.length} Entities
                          </span>
                        </div>
                      ) : (
                        <div className={style.entityInfo}>
                          <div className={style.entityName}>
                            {assignedEntities[0]?.name ? (
                              <PlainLink
                                href={assignedEntities[0].href}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {assignedEntities[0].name}
                              </PlainLink>
                            ) : (
                              '--'
                            )}
                          </div>
                          <div className={style.entityAddress}>
                            <span className={style.label}>Address:</span>
                            <span className={style.value}>
                              {' '}
                              {assignedEntities[0]?.address || '--'}
                            </span>
                          </div>
                          <div className={style.entityType}>
                            <span className={style.label}>Entity Type:</span>
                            <span className={style.value}>
                              {' '}
                              {assignedEntities[0]?.type || '--'}
                            </span>
                          </div>
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className={style.accountItem}>
                        <div className={style.accountName}>
                          <PlainLink
                            href={assignedAccount.salesforceHref}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {assignedAccount.name}
                          </PlainLink>
                        </div>
                        <div className={style.accountAddress}>
                          <span className={style.label}>Address:</span>
                          <span className={style.value}>
                            {' '}
                            {assignedAccount.billingAddress || '--'}
                          </span>
                        </div>
                        <div className={style.accountType}>
                          <span className={style.label}>Type:</span>
                          <span className={style.value}>
                            {' '}
                            {assignedAccount.type || '--'}
                          </span>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                  {hasMultipleEntities && isAssignedEntitiesExpanded && (
                    <TableRow>
                      <TableCell colSpan={2}>
                        <div className={style.subTableWrapper}>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableHeader
                                  className={style.subTableHeaderCell}
                                >
                                  Insights Entity
                                </TableHeader>
                                <TableHeader
                                  className={style.subTableHeaderCell}
                                >
                                  Type
                                </TableHeader>
                                <TableHeader
                                  className={style.subTableHeaderCell}
                                >
                                  Address
                                </TableHeader>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {assignedEntities.map(
                                (
                                  entity: {
                                    id: number;
                                    name: string;
                                    href: string;
                                    type: string | null;
                                    address: string | null;
                                  },
                                  index: number
                                ) => (
                                  <TableRow key={entity.id || index}>
                                    <TableCell>
                                      <div className={style.entityName}>
                                        <PlainLink
                                          href={entity.href || '#'}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                        >
                                          {entity.name || '--'}
                                        </PlainLink>
                                      </div>
                                    </TableCell>
                                    <TableCell>{entity.type || '--'}</TableCell>
                                    <TableCell>
                                      {entity.address || '--'}
                                    </TableCell>
                                  </TableRow>
                                )
                              )}
                            </TableBody>
                          </Table>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}

          <div className={style.searchHeader}>
            <div className={style.searchTitle}>
              <h3 className={style.heading}>Select Entity*</h3>
              <p className={style.subheading}>
                Search for your desired Entity and select it to continue.
              </p>
            </div>

            <div className={style.filterCheckbox}>
              <Checkbox
                id="linked-only-filter"
                labelText="Show Linked Entities First"
                checked={linkedOnly}
                onChange={(_evt, { checked }: { checked: boolean }) =>
                  setLinkedOnly(checked)
                }
              />
            </div>
          </div>

          <EntitySearchTable
            searchAtom={searchAtom}
            baseParams={{ linkedOnly }}
            persistentPagination={persistentPagination}
            onEntitySelect={handleEntitySelect}
            onAccountSelect={handleAccountSelect}
            defaultPageSize={DEFAULT_PAGE_SIZE}
            minSearchLength={0}
            renderActions={(selectedEntityForActions) => (
              <>
                <Button
                  kind="primary"
                  size="sm"
                  renderIcon={Add}
                  iconDescription="Create CRM Account"
                  disabled={
                    !canOpenLinkPanel ||
                    showCreatePanel ||
                    showLinkPanel ||
                    isLinking
                  }
                  onClick={() => {
                    if (!selectedEntityForActions) return;
                    setShowLinkPanel(false);
                    resetLinkingNotifications();
                    resetCreateAccountState();
                    setShowCreatePanel(true);
                  }}
                >
                  Create CRM Account
                </Button>
                <Button
                  size="sm"
                  renderIcon={Connect}
                  iconDescription="Link CRM Account"
                  disabled={
                    !canOpenLinkPanel ||
                    showLinkPanel ||
                    showCreatePanel ||
                    isLinking
                  }
                  onClick={() => {
                    if (!selectedEntityForActions) return;
                    setShowCreatePanel(false);
                    resetLinkingNotifications();
                    resetCreateAccountState();
                    setShowLinkPanel(true);
                  }}
                >
                  Link CRM Account
                </Button>
              </>
            )}
          />
        </div>

        {/* Create CRM Account Panel */}
        {showCreatePanel && (
          <div className={style.linkPanelWrapper}>
            <CreateCrmAccount
              isOpen={showCreatePanel}
              initialAccountName={selectedEntityData?.name || ''}
              accountTypeOptions={accountType}
              accountSourceOptions={accountSource}
              onClose={() => {
                setShowCreatePanel(false);
                resetCreateAccountState();
                resetLinkingNotifications();
              }}
              onCreate={(payload) => {
                setCreateAccountError(null);
                setIsCreatingAccount(true);
                createCrmAccountMutationState({
                  url: '/crm/accounts',
                  type: 'POST',
                  data: {
                    name: payload.accountName,
                    type: payload.type,
                    source: payload.accountSource,
                    phone: payload.phoneNumber,
                    address: payload.address,
                    city: payload.city,
                    state: payload.state,
                    zip_code: payload.zipCode,
                  },
                  onSuccess: (response) => {
                    const channel = response.resources?.notifications?.channel;
                    const events =
                      response.resources?.notifications?.events || [];
                    if (channel) {
                      setCreateAccountChannel(channel);
                      setCreateAccountEvents(events);
                    }
                  },
                  onError: (error: unknown) => {
                    setIsCreatingAccount(false);
                    const errorMessage =
                      error instanceof Error
                        ? error.message
                        : 'Failed to create CRM account. Please try again.';
                    setCreateAccountError(errorMessage);
                  },
                });
              }}
              createAccountError={createAccountError}
              isCreatingAccount={isCreatingAccount}
            />
          </div>
        )}

        {/* Link CRM Account Panel */}
        {showLinkPanel && (
          <div className={style.linkPanelWrapper}>
            <LinkCrmAccountPanel
              isOpen={showLinkPanel}
              selectedEntity={selectedEntity}
              initialSearchText={selectedEntityData?.name || ''}
              onClose={() => {
                setShowLinkPanel(false);
                setSelectedEntity(null);
                resetLinkingNotifications();
              }}
              onLinkSuccess={(entityId, accountId, data) => {
                const { linkDetails, channel } =
                  processEntityLinkResponse(data);

                setEntityLinkStatus({
                  entityId: entityId,
                  channel: channel,
                  linkDetails: linkDetails,
                  linkedAccountId: accountId,
                });
                setOverallStatus('pending');
                setShowLinkPanel(false);
              }}
              onLinkError={(error) => {
                setShowLinkPanel(false);
                setEntityLinkStatus({
                  entityId: null,
                  channel: null,
                  linkDetails: null,
                  linkedAccountId: null,
                });
                setLinkingError(getLinkingErrorMessage(error));
              }}
              onLinkStart={(accountId) => {
                setIsLinkWorkflowComplete(false);
                setOverallStatus('pending');
                resetLinkingNotifications();
                if (selectedEntity) {
                  setEntityLinkStatus({
                    entityId: selectedEntity,
                    channel: null,
                    linkDetails: null,
                    linkedAccountId: accountId,
                  });
                }
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export { EntityAccountPicker };
